/**
 * @fileoverview Scenario Page
 * @description Run history for one scenario, including trend and comparison.
 */

import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  Database,
  Gauge,
  ListChecks,
  Play,
  Plus,
  Sigma,
  TrendingDown,
  Trash2,
} from 'lucide-react';
import {
  DeltaIndicator,
  EmptyState,
  EvaluationBreadcrumbs,
  MissingRecord,
  ScoreChip,
  SummaryMetric,
} from '../components/common';
import { RunComparison } from '../components/RunComparison';
import { deleteRun, ingestRun, scenarioStats } from '../utils/evaluationStore';
import { bandForScore, toEvaluatorView } from '../utils/evaluationView';
import { parseEvaluationResults } from '../utils/evaluationParser';

/**
 * Render the run history for one scenario.
 * @param {Object} props - Workspace projects and change handler.
 * @returns {JSX.Element} Scenario page.
 */
export function ScenarioPage({ projects, onProjectsChange }) {
  const { projectId, cycleId, scenarioId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [baselineRunId, setBaselineRunId] = useState('');
  const [compareRunId, setCompareRunId] = useState('');

  const project = projects.find((item) => item.id === projectId);
  const cycle = project?.cycles.find((item) => item.id === cycleId);
  const scenario = cycle?.scenarios.find((item) => item.id === scenarioId);

  if (!project || !cycle || !scenario) {
    return <MissingRecord icon={ListChecks} message="This scenario no longer exists." />;
  }

  const stats = scenarioStats(scenario);
  const runs = scenario.runs;

  // Default to the two most recent runs, and recover if a selected run is deleted.
  const resolvedCompareId = runs.some((run) => run.id === compareRunId)
    ? compareRunId
    : runs[runs.length - 1]?.id || '';
  const resolvedBaselineId = runs.some((run) => run.id === baselineRunId) && baselineRunId !== resolvedCompareId
    ? baselineRunId
    : runs.filter((run) => run.id !== resolvedCompareId).slice(-1)[0]?.id || '';

  /**
   * Read an evaluation result file and record it as a run.
   * @param {File} file - Selected evaluation file.
   * @returns {Promise<void>} Resolves once the run is stored.
   */
  const addRunFromFile = async (file) => {
    try {
      const view = toEvaluatorView(parseEvaluationResults(await file.text(), file.name));
      const placement = ingestRun(view, {
        projectId: project.id,
        cycleId: cycle.id,
        scenarioId: scenario.id,
      });
      onProjectsChange(placement.projects);
      setErrorMessage('');

      if (placement.scenarioId !== scenario.id) {
        navigate(`/ai-evaluation-results/${placement.projectId}/${placement.cycleId}/${placement.scenarioId}`);
      }
    } catch (parseError) {
      setErrorMessage(parseError.message);
    }
  };

  /**
   * Accept a dropped evaluation file.
   * @param {React.DragEvent} event - Drop event.
   * @returns {void}
   */
  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      addRunFromFile(file);
    }
  };

  const addRunButton = (
    <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => fileInputRef.current?.click()}>
      Add run
    </Button>
  );

  const trendValues = runs.map((run) => Number(run.summary.overallAverage.toFixed(3)));
  const trendLabels = runs.map((run, index) => (
    run.generatedAt || run.importedAt
      ? new Date(run.generatedAt || run.importedAt).toLocaleDateString()
      : `Run ${index + 1}`
  ));
  const averageScore = runs.length
    ? runs.reduce((sum, run) => sum + run.summary.overallAverage, 0) / runs.length
    : null;

  return (
    <Box
      onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={isDragging ? 'aier-dropzone-active' : undefined}
    >
      <EvaluationBreadcrumbs
        trail={[
          { label: project.name, to: `/ai-evaluation-results/${project.id}` },
          { label: cycle.name, to: `/ai-evaluation-results/${project.id}/${cycle.id}` },
          { label: scenario.name },
        ]}
      />

      <Box className="aier-header">
        <Box>
          <Typography variant="h4" component="h1">{scenario.name}</Typography>
          <Typography color="text.secondary">
            {scenario.description || 'Every run of this scenario, with its scores and history.'}
          </Typography>
        </Box>
        {runs.length > 0 && addRunButton}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        data-testid="aier-file-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            addRunFromFile(file);
          }
          event.target.value = '';
        }}
      />

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')}>{errorMessage}</Alert>
      )}

      {runs.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No runs yet"
          message="Runs for this scenario will appear here. Each run keeps its scores, results, and conversation so you can track how the assistant moves over time."
          hint="Supports scenario evaluation runs and consolidated evaluator reports."
        >
          {addRunButton}
        </EmptyState>
      ) : (
        <>
          <Box className="aier-summary-grid aier-summary-grid--four" aria-label="Scenario summary">
            <SummaryMetric
              label="Latest score"
              value={stats.latestScore === null ? '—' : stats.latestScore.toFixed(2)}
              caption={stats.delta === null ? 'First run' : `${stats.delta >= 0 ? '+' : ''}${stats.delta.toFixed(2)} vs previous`}
              icon={Gauge}
              color="#01426A"
            />
            <SummaryMetric
              label="Average score"
              value={averageScore === null ? '—' : averageScore.toFixed(2)}
              caption={`Mean across ${runs.length} ${runs.length === 1 ? 'run' : 'runs'}`}
              icon={Sigma}
              color="#008653"
            />
            <SummaryMetric
              label="Runs"
              value={stats.runCount}
              caption={`Latest ${new Date(stats.latestRun.importedAt).toLocaleDateString()}`}
              icon={Play}
              color="#0097A9"
            />
            <SummaryMetric
              label="Below target"
              value={stats.belowTarget}
              caption="In the latest run"
              icon={TrendingDown}
              color="#C81E3A"
            />
          </Box>

          <Paper className="aier-trend" elevation={0}>
            <Box className="aier-trend-header">
              <Box>
                <Typography className="aier-eyebrow">Score trend</Typography>
                <Typography variant="h6">Overall average across {runs.length} runs</Typography>
              </Box>
            </Box>
            {runs.length < 2 ? (
              <Box className="aier-no-results">
                <Typography color="text.secondary">At least two runs are needed to show the trend line.</Typography>
              </Box>
            ) : (
              <LineChart
                height={240}
                series={[{ data: trendValues, label: 'Overall average', color: '#0097A9', curve: 'linear', showMark: true }]}
                xAxis={[{ scaleType: 'point', data: trendLabels }]}
                yAxis={[{ min: 0, max: 1 }]}
                margin={{ top: 20, right: 20, bottom: 30, left: 44 }}
              />
            )}
          </Paper>

          <Paper className="aier-results" elevation={0}>
            <Box className="aier-toolbar">
              <Box>
                <Typography variant="h6">Runs</Typography>
                <Typography className="aier-toolbar-count">
                  Newest first · movement compares each run with the one before it
                </Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small" className="aier-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Recorded</TableCell>
                    <TableCell align="center">Overall</TableCell>
                    <TableCell align="center">Results</TableCell>
                    <TableCell align="center">Movement</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...runs].reverse().map((run) => {
                    const position = runs.findIndex((item) => item.id === run.id);
                    const previous = position > 0 ? runs[position - 1] : null;
                    const delta = previous
                      ? run.summary.overallAverage - previous.summary.overallAverage
                      : null;

                    return (
                      <TableRow key={run.id} hover>
                        <TableCell className="aier-cell-field">{run.label}</TableCell>
                        <TableCell className="aier-cell-muted">
                          {run.generatedAt ? new Date(run.generatedAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="aier-cell-muted">
                          {new Date(run.importedAt).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <ScoreChip
                            score={run.summary.overallAverage}
                            band={bandForScore(run.summary.overallAverage)}
                          />
                        </TableCell>
                        <TableCell align="center" className="aier-cell-muted">
                          {run.summary.passed === undefined
                            ? `${run.summary.evaluatorsScored} scored`
                            : `${run.summary.passed} passed · ${run.summary.failed} failed`}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" justifyContent="center">
                            <DeltaIndicator delta={delta} />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" onClick={() => navigate(run.id)}>Open</Button>
                            <Tooltip title="Delete run">
                              <IconButton
                                size="small"
                                onClick={() => onProjectsChange(deleteRun(project.id, cycle.id, scenario.id, run.id))}
                                aria-label={`Delete run ${run.label}`}
                              >
                                <Trash2 size={15} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {runs.length > 0 && (
            <RunComparison
              runs={runs}
              baselineId={resolvedBaselineId}
              currentId={resolvedCompareId}
              onBaselineChange={setBaselineRunId}
              onCurrentChange={setCompareRunId}
              onSwap={() => {
                setBaselineRunId(resolvedCompareId);
                setCompareRunId(resolvedBaselineId);
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default ScenarioPage;
